import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AccountService } from "../services";
import { ActivatedRoute, Router } from "@angular/router";
import {
  catchError,
  delay,
  finalize,
  map,
  takeUntil,
  tap,
} from "rxjs/operators";
import { of, Subject } from "rxjs";
@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
  styleUrls: ["./change-password.component.scss"],
})
export class ChangePasswordComponent implements OnInit {
  changePasswordForm: FormGroup;
  submittedPassword: boolean = false;
  disabled: boolean = false;
  token: string;
  isTokenValid: boolean = false;
  tokenVerified: any;
  loading: { email: boolean; password: boolean } = {
    email: false,
    password: false,
  };
  showCurrentPassword: boolean = true;
  showNewPassword: boolean = true;
  showConfirmPassword: boolean = true;
  errorsMail: { status: boolean; mess: string } = null;
  errorsPass: { status: boolean; mess: string } = null;
  onDestroy$: Subject<void> = new Subject<void>();
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly accountService: AccountService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  get fP() {
    return this.changePasswordForm.controls;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.token = params["id"] ?? null;
      if (!this.token) {
        this.errorsPass = {
          status: true,
          mess: "Invalid or missing token. Please request a new password reset link.",
        };
        this.isTokenValid = false;
      } else {
        this._verifyToken();
      }
    });

    this.changePasswordForm = this.formBuilder.group({
      currentPassword: ["", Validators.required],
      newPassword: [
        "",
        [
          Validators.required,
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@!$%&*])[A-Za-z\d@!$%&*]{10,}$/
          ),
        ],
      ],
      confirmNewPassword: ["", Validators.required],
    });
  }

  onChangePasswordFormSubmit() {
    this.submittedPassword = true;
    if (!this.changePasswordForm.invalid) {
      const { currentPassword, newPassword, confirmNewPassword } =
        this.changePasswordForm.controls;
      if (newPassword.value !== confirmNewPassword.value) {
        this.errorsPass = {
          status: true,
          mess: "New Password and Confirm Password don't match. Please try again!",
        };
        return;
      }
      this.disabled = true;
      this.loading.password = true;
      this.errorsPass = null;
      this._handleChangePassword(
        currentPassword.value,
        newPassword.value,
        confirmNewPassword.value
      );
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  _verifyToken() {
    this.accountService
      .verifyToken(this.token)
      .pipe(
        takeUntil(this.onDestroy$),
        map((resp) => {
          if (resp?.result?.tokenValid) {
            this.tokenVerified = resp.result;
            this.isTokenValid = true;
          } else {
            this.errorsPass = {
              status: true,
              mess: "This link has expired. Please use the latest link received or change password from your account settings.",
            };
            this.isTokenValid = false;
          }
        }),
        catchError(() => {
          this.errorsPass = {
            status: true,
            mess: "Error verifying token. Please try again.",
          };
          this.isTokenValid = false;
          return of(null);
        })
      )
      .subscribe();
  }

  _handleChangePassword(currentPassword, newPassword, confirmNewPassword) {
    this.accountService
      .changePasswordViaLink({
        CurrentPassword: currentPassword,
        NewPassword: newPassword,
        ConfirmPassword: confirmNewPassword,
        UserName: this.tokenVerified.userName,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          this.errorsPass = {
            status: false,
            mess: "Your password has been changed successfully",
          };
        }),
        delay(3000),
        tap(() => {
          this.router.navigate(["/login"]);
        }),
        catchError((err) => {
          this.errorsPass = { status: true, mess: err.error.message };
          return of(null);
        }),
        finalize(() => {
          this.disabled = false;
          this.loading.password = false;
        })
      )
      .subscribe();
  }
}
